import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Users, Settings, Building2, Database, Code, FileText, ChevronDown, ChevronRight, Eye, EyeOff, Search, Filter, MoreVertical, Check, AlertCircle, Info } from 'lucide-react';
import { DataService, Organization, Collection, SchemaField, CollectionSchema } from './services/DataService';
import SchemaFieldEditor from './SchemaFieldEditor';
import { useAuth } from '../hooks/AuthProvider';

// Form data interfaces
interface OrganizationFormData {
  id: string;
  name: string;
  collections: Collection[];
  roles: string[];
}

interface SchemaFormData {
  fields: Record<string, SchemaField>;
  version: number;
}

const DEFAULT_ROLES = ['admin', 'read', 'write', 'api'];

const OrganizationsManager: React.FC = () => {
  const { currentUser } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editingSchema, setEditingSchema] = useState<{ orgId: string; collectionId: string; collection: Collection } | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [deleteConfirmOrg, setDeleteConfirmOrg] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState<OrganizationFormData>({
    id: '',
    name: '',
    collections: [],
    roles: [...DEFAULT_ROLES]
  });

  // Schema form state
  const [schemaFormData, setSchemaFormData] = useState<SchemaFormData>({
    fields: {},
    version: 1
  });

  // Schema editor state
  const [isRawMode, setIsRawMode] = useState(false);
  const [rawSchemaJson, setRawSchemaJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string>('');
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [fieldSearchTerm, setFieldSearchTerm] = useState('');

  // Load organizations on component mount
  useEffect(() => {
    loadOrganizations();
  }, []);

  // Auto-dismiss messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 7000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Update raw JSON when schema form data changes
  useEffect(() => {
    if (!isRawMode) {
      setRawSchemaJson(JSON.stringify(schemaFormData, null, 2));
    }
  }, [schemaFormData, isRawMode]);

  const loadOrganizations = async () => {
    try {
      setInitialLoading(true);
      const orgsData = await DataService.getOrganizations();
      setOrganizations(orgsData);
    } catch (err: any) {
      console.error('Error loading organizations:', err);
      setError(err.message || 'Failed to load organizations');
    } finally {
      setInitialLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      collections: [],
      roles: [...DEFAULT_ROLES]
    });
  };

  const resetSchemaForm = () => {
    setSchemaFormData({
      fields: {},
      version: 1
    });
    setExpandedFields(new Set());
    setIsRawMode(false);
    setRawSchemaJson('');
    setJsonError('');
    setFieldSearchTerm('');
  };

  const handleCreateOrganization = async () => {
    if (!formData.id || !formData.name) {
      setError('Organization ID and name are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await DataService.createOrganization(
        formData.id,
        formData.name,
        formData.collections,
        formData.roles
      );
      await loadOrganizations();
      setSuccess('Organization created successfully');
      setIsCreateModalOpen(false);
      resetForm();
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!editingOrg || !formData.name) {
      setError('Organization name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await DataService.updateOrganization(editingOrg.id, {
        name: formData.name,
        collections: formData.collections,
        roles: formData.roles
      });
      await loadOrganizations();
      setSuccess('Organization updated successfully');
      setEditingOrg(null);
      resetForm();
    } catch (err: any) {
      console.error('Error updating organization:', err);
      setError(err.message || 'Failed to update organization');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrganization = async (orgId: string) => {
    setLoading(true);
    setError('');

    try {
      await DataService.deleteOrganization(orgId);
      await loadOrganizations();
      setSuccess('Organization deleted successfully');
      setDeleteConfirmOrg(null);
    } catch (err: any) {
      console.error('Error deleting organization:', err);
      setError(err.message || 'Failed to delete organization');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchema = async () => {
    if (!editingSchema) return;

    if (isRawMode) {
      try {
        const parsedSchema = JSON.parse(rawSchemaJson);
        setSchemaFormData(parsedSchema);
        setJsonError('');
      } catch (err) {
        setJsonError('Invalid JSON format');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const schema: CollectionSchema = {
        fields: schemaFormData.fields,
        version: schemaFormData.version
      };

      await DataService.updateCollectionSchema(
        editingSchema.orgId,
        editingSchema.collectionId,
        schema
      );
      await loadOrganizations();
      setSuccess('Collection schema updated successfully');
      setEditingSchema(null);
      resetSchemaForm();
    } catch (err: any) {
      console.error('Error updating schema:', err);
      setError(err.message || 'Failed to update schema');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (org: Organization) => {
    setEditingOrg(org);
    setFormData({
      id: org.id,
      name: org.name,
      collections: org.collections || [],
      roles: org.roles || [...DEFAULT_ROLES]
    });
  };

  const startEditingSchema = async (orgId: string, collectionId: string, collection: Collection) => {
    try {
      setLoading(true);
      
      const existingSchema = await DataService.getCollectionSchema(orgId, collectionId);
      
      setEditingSchema({ orgId, collectionId, collection });
      const schemaData = {
        fields: existingSchema?.fields || collection.schema?.fields || {},
        version: existingSchema?.version || collection.schema?.version || 1
      };
      
      setSchemaFormData(schemaData);
      setRawSchemaJson(JSON.stringify(schemaData, null, 2));
    } catch (err: any) {
      console.error('Error loading schema:', err);
      setError(err.message || 'Failed to load schema');
    } finally {
      setLoading(false);
    }
  };

  const toggleSchemaMode = () => {
    if (isRawMode) {
      try {
        const parsedSchema = JSON.parse(rawSchemaJson);
        setSchemaFormData(parsedSchema);
        setJsonError('');
        setIsRawMode(false);
      } catch (err) {
        setJsonError('Invalid JSON format. Please fix the JSON before switching to UI mode.');
        return;
      }
    } else {
      setRawSchemaJson(JSON.stringify(schemaFormData, null, 2));
      setIsRawMode(true);
      setJsonError('');
    }
  };

  const handleRawJsonChange = (value: string) => {
    setRawSchemaJson(value);
    setJsonError('');
  };

  // Collection management functions
  const addCollection = () => {
    setFormData({
      ...formData,
      collections: [...formData.collections, { id: '', name: '' }]
    });
  };

  const updateCollection = (index: number, field: 'id' | 'name', value: string) => {
    const updatedCollections = formData.collections.map((collection, i) =>
      i === index ? { ...collection, [field]: value } : collection
    );
    setFormData({ ...formData, collections: updatedCollections });
  };

  const removeCollection = (index: number) => {
    setFormData({
      ...formData,
      collections: formData.collections.filter((_, i) => i !== index)
    });
  };

  // Role management functions
  const addRole = () => {
    const newRole = window.prompt('Enter new role name:');
    if (newRole && !formData.roles.includes(newRole)) {
      setFormData({
        ...formData,
        roles: [...formData.roles, newRole]
      });
    }
  };

  const removeRole = (role: string) => {
    setFormData({
      ...formData,
      roles: formData.roles.filter(r => r !== role)
    });
  };

  // Schema field management functions
  const addSchemaField = () => {
    const fieldName = window.prompt('Enter field name:');
    if (fieldName && !schemaFormData.fields[fieldName]) {
      setSchemaFormData({
        ...schemaFormData,
        fields: {
          ...schemaFormData.fields,
          [fieldName]: DataService.createSchemaField('string')
        }
      });
      const newExpanded = new Set(expandedFields);
      newExpanded.add(fieldName);
      setExpandedFields(newExpanded);
    }
  };

  const updateSchemaField = (fieldPath: string, updates: Partial<SchemaField>) => {
    const pathParts = fieldPath.split('.');
    const newFields = { ...schemaFormData.fields };
    
    if (pathParts.length === 1) {
      newFields[pathParts[0]] = { ...newFields[pathParts[0]], ...updates };
    } else {
      const topLevelField = pathParts[0];
      const nestedPath = pathParts.slice(1);
      newFields[topLevelField] = updateNestedField(newFields[topLevelField], nestedPath, updates);
    }
    
    setSchemaFormData({
      ...schemaFormData,
      fields: newFields
    });
  };

  const updateNestedField = (field: SchemaField, path: string[], updates: Partial<SchemaField>): SchemaField => {
    if (path.length === 0) {
      return { ...field, ...updates };
    }
    
    const [currentKey, ...remainingPath] = path;
    
    if (field.type === 'array' && currentKey === 'arrayItemType' && field.arrayItemType) {
      return {
        ...field,
        arrayItemType: updateNestedField(field.arrayItemType, remainingPath, updates)
      };
    } else if (field.type === 'object' && field.objectSchema && currentKey in field.objectSchema) {
      return {
        ...field,
        objectSchema: {
          ...field.objectSchema,
          [currentKey]: updateNestedField(field.objectSchema[currentKey], remainingPath, updates)
        }
      };
    }
    
    return field;
  };

  const removeSchemaField = (fieldName: string) => {
    const { [fieldName]: removed, ...remainingFields } = schemaFormData.fields;
    setSchemaFormData({
      ...schemaFormData,
      fields: remainingFields
    });
  };

  const addNestedObjectField = (parentPath: string) => {
    const fieldName = window.prompt('Enter field name:');
    if (!fieldName) return;
    
    const pathParts = parentPath.split('.');
    const newFields = { ...schemaFormData.fields };
    
    if (pathParts.length === 1) {
      const parentField = newFields[pathParts[0]];
      if (parentField.type === 'object') {
        newFields[pathParts[0]] = {
          ...parentField,
          objectSchema: {
            ...parentField.objectSchema,
            [fieldName]: DataService.createSchemaField('string')
          }
        };
      }
    }
    
    setSchemaFormData({
      ...schemaFormData,
      fields: newFields
    });
  };

  const toggleFieldExpansion = (fieldPath: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldPath)) {
      newExpanded.delete(fieldPath);
    } else {
      newExpanded.add(fieldPath);
    }
    setExpandedFields(newExpanded);
  };

  // Schema field type colors
  const getTypeColor = (type: string) => {
    const colors = {
      string: '#10b981',
      number: '#3b82f6',
      boolean: '#f59e0b',
      date: '#8b5cf6',
      array: '#ef4444',
      object: '#6366f1',
      reference: '#ec4899'
    };
    return colors[type as keyof typeof colors] || '#6b7280';
  };

  // Filter organizations based on search
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter schema fields based on search
  const filteredSchemaFields = Object.entries(schemaFormData.fields).filter(([fieldName]) =>
    fieldName.toLowerCase().includes(fieldSearchTerm.toLowerCase())
  );

  // Loading state render
  if (initialLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px'
          }}></div>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
            Loading Organizations
          </h2>
          <p style={{ color: '#64748b', fontSize: '16px' }}>Please wait while we load your organization data</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Modern Header */}
      <div style={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e2e8f0', 
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 40
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: '40px',
                height: '40px',
                backgroundColor: '#3b82f6',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px'
              }}>
                <Building2 style={{ width: '24px', height: '24px', color: 'white' }} />
              </div>
              <div>
                <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0, lineHeight: '1.2' }}>
                  Organizations
                </h1>
                <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                  Manage organizations, collections, and data schemas
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: '#fef3c7',
                color: '#92400e',
                border: '1px solid #fde68a'
              }}>
                <Settings size={16} style={{ marginRight: '6px' }} />
                APP_ADMIN
              </div>
              <span style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                {currentUser?.email}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Alerts */}
      {error && (
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px 24px 0' }}>
          <div style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <AlertCircle size={20} style={{ color: '#dc2626', marginTop: '2px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#dc2626' }}>
                Error
              </h4>
              <p style={{ fontSize: '14px', color: '#991b1b', margin: 0 }}>{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              style={{
                color: '#dc2626',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px 24px 0' }}>
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <Check size={20} style={{ color: '#16a34a', marginTop: '2px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#16a34a' }}>
                Success
              </h4>
              <p style={{ fontSize: '14px', color: '#15803d', margin: 0 }}>{success}</p>
            </div>
            <button
              onClick={() => setSuccess('')}
              style={{
                color: '#16a34a',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.1)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Enhanced Header Section */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: '800', color: '#1e293b', marginBottom: '8px', lineHeight: '1.2' }}>
                Organization Management
              </h2>
              <p style={{ color: '#64748b', fontSize: '18px', marginBottom: '0', lineHeight: '1.6' }}>
                Create and manage organizations, define collections, and configure data schemas with ease.
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setIsCreateModalOpen(true);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }}
              disabled={loading}
            >
              <Plus size={20} style={{ marginRight: '8px' }} />
              Create Organization
            </button>
          </div>

          {/* Search and Filter Bar */}
          <div style={{ 
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                <Search size={20} style={{ 
                  position: 'absolute', 
                  left: '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  color: '#9ca3af' 
                }} />
                <input
                  type="text"
                  placeholder="Search organizations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 48px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '16px',
                    transition: 'border-color 0.2s ease',
                    backgroundColor: '#f9fafb'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.backgroundColor = 'white';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#f9fafb';
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#64748b', fontSize: '14px' }}>
                <Filter size={16} />
                <span>{filteredOrganizations.length} organization{filteredOrganizations.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Organizations Grid */}
        <div style={{ 
          display: 'grid', 
          gap: '24px', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))'
        }}>
          {filteredOrganizations.map((org) => (
            <div key={org.id} style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.1)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
            }}
            >
              {/* Organization Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '4px', lineHeight: '1.3' }}>
                    {org.name}
                  </h3>
                  <p style={{ fontSize: '14px', color: '#64748b', fontFamily: 'monospace', backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', display: 'inline-block' }}>
                    {org.id}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '4px', marginLeft: '16px' }}>
                  <button
                    onClick={() => startEditing(org)}
                    style={{
                      padding: '8px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#475569',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#dbeafe';
                      e.currentTarget.style.borderColor = '#93c5fd';
                      e.currentTarget.style.color = '#1d4ed8';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.color = '#475569';
                    }}
                    disabled={loading}
                    title="Edit organization"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirmOrg(org.id)}
                    style={{
                      padding: '8px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: '#475569',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                      e.currentTarget.style.borderColor = '#fca5a5';
                      e.currentTarget.style.color = '#dc2626';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.color = '#475569';
                    }}
                    disabled={loading}
                    title="Delete organization"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Enhanced Collections Section */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#1e293b', 
                    display: 'flex', 
                    alignItems: 'center',
                    margin: 0
                  }}>
                    <Database size={18} style={{ marginRight: '8px', color: '#64748b' }} />
                    Collections
                  </h4>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: '600',
                    color: '#64748b', 
                    backgroundColor: '#f1f5f9', 
                    padding: '4px 12px', 
                    borderRadius: '12px',
                    border: '1px solid #cbd5e1'
                  }}>
                    {org.collections?.length || 0}
                  </span>
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {org.collections?.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {org.collections.map((collection) => (
                        <div key={collection.id} style={{
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          padding: '16px',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <h5 style={{ fontWeight: '600', color: '#1e293b', margin: '0 0 4px 0', fontSize: '14px' }}>
                                {collection.name}
                              </h5>
                              <p style={{ color: '#64748b', fontSize: '12px', fontFamily: 'monospace', margin: 0 }}>
                                {collection.id}
                              </p>
                            </div>
                            <button
                              onClick={() => startEditingSchema(org.id, collection.id, collection)}
                              style={{
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                backgroundColor: collection.schema ? '#dbeafe' : '#fef3c7',
                                color: collection.schema ? '#1e40af' : '#92400e',
                                border: '1px solid',
                                borderColor: collection.schema ? '#93c5fd' : '#fde68a',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseOver={(e) => {
                                if (collection.schema) {
                                  e.currentTarget.style.backgroundColor = '#bfdbfe';
                                  e.currentTarget.style.borderColor = '#60a5fa';
                                } else {
                                  e.currentTarget.style.backgroundColor = '#fde68a';
                                  e.currentTarget.style.borderColor = '#f59e0b';
                                }
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = collection.schema ? '#dbeafe' : '#fef3c7';
                                e.currentTarget.style.borderColor = collection.schema ? '#93c5fd' : '#fde68a';
                              }}
                              disabled={loading}
                            >
                              {collection.schema ? 'Edit Schema' : 'Add Schema'}
                            </button>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Code size={14} style={{ color: '#64748b' }} />
                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                              {collection.schema ? 
                                `${Object.keys(collection.schema.fields).length} field${Object.keys(collection.schema.fields).length !== 1 ? 's' : ''}` : 
                                'No schema defined'
                              }
                            </span>
                            {collection.schema && (
                              <span style={{
                                fontSize: '10px',
                                fontWeight: '600',
                                color: '#059669',
                                backgroundColor: '#d1fae5',
                                padding: '2px 6px',
                                borderRadius: '6px',
                                marginLeft: 'auto'
                              }}>
                                v{collection.schema.version}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      border: '2px dashed #cbd5e1'
                    }}>
                      <Database size={24} style={{ color: '#cbd5e1', margin: '0 auto 8px' }} />
                      <p style={{ fontSize: '14px', color: '#64748b', margin: 0, fontStyle: 'italic' }}>
                        No collections configured
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Roles Section */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#1e293b', 
                    display: 'flex', 
                    alignItems: 'center',
                    margin: 0
                  }}>
                    <Users size={18} style={{ marginRight: '8px', color: '#64748b' }} />
                    Roles
                  </h4>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: '600',
                    color: '#64748b', 
                    backgroundColor: '#f1f5f9', 
                    padding: '4px 12px', 
                    borderRadius: '12px',
                    border: '1px solid #cbd5e1'
                  }}>
                    {org.roles?.length || 0}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {org.roles?.length ? (
                    org.roles.map((role) => (
                      <span key={role} style={{
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        padding: '6px 12px',
                        borderRadius: '16px',
                        border: '1px solid #93c5fd',
                        transition: 'all 0.2s ease'
                      }}>
                        {role}
                      </span>
                    ))
                  ) : (
                    <div style={{ 
                      width: '100%',
                      textAlign: 'center', 
                      padding: '16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px dashed #cbd5e1'
                    }}>
                      <p style={{ fontSize: '13px', color: '#64748b', margin: 0, fontStyle: 'italic' }}>
                        No roles configured
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Organization Status Indicator */}
              <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                width: '8px',
                height: '8px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                boxShadow: '0 0 0 2px white, 0 0 0 4px #d1fae5'
              }}></div>
            </div>
          ))}

          {/* Empty State */}
          {filteredOrganizations.length === 0 && organizations.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{
                textAlign: 'center',
                padding: '48px 24px',
                backgroundColor: 'white',
                borderRadius: '20px',
                border: '1px solid #e2e8f0'
              }}>
                <Search size={48} style={{ color: '#cbd5e1', margin: '0 auto 16px' }} />
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>
                  No organizations found
                </h3>
                <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '16px' }}>
                  No organizations match your search criteria. Try adjusting your search terms.
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#e2e8f0';
                    e.currentTarget.style.borderColor = '#94a3b8';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                >
                  Clear Search
                </button>
              </div>
            </div>
          )}

          {filteredOrganizations.length === 0 && organizations.length === 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{
                textAlign: 'center',
                padding: '64px 24px',
                backgroundColor: 'white',
                borderRadius: '20px',
                border: '2px dashed #cbd5e1',
                background: 'linear-gradient(45deg, #f8fafc 25%, transparent 25%), linear-gradient(-45deg, #f8fafc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f8fafc 75%), linear-gradient(-45deg, transparent 75%, #f8fafc 75%)',
                backgroundSize: '20px 20px',
                backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
              }}>
                <Building2 size={64} style={{ color: '#cbd5e1', margin: '0 auto 24px' }} />
                <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
                  Welcome to Organizations
                </h3>
                <p style={{ color: '#64748b', marginBottom: '32px', fontSize: '18px', maxWidth: '400px', margin: '0 auto 32px' }}>
                  Get started by creating your first organization. Define collections, schemas, and manage access roles all in one place.
                </p>
                <button
                  onClick={() => {
                    resetForm();
                    setIsCreateModalOpen(true);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '16px 32px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    fontWeight: '600',
                    fontSize: '18px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#2563eb';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(59, 130, 246, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.3)';
                  }}
                >
                  <Plus size={24} style={{ marginRight: '12px' }} />
                  Create Your First Organization
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

{/* Enhanced Schema Editor Modal - Full Screen */}
{editingSchema && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'center',
          zIndex: 50,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Enhanced Modal Header - Fixed */}
            <div style={{ 
              padding: '24px 32px', 
              borderBottom: '1px solid #e2e8f0', 
              flexShrink: 0,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FileText size={28} style={{ marginRight: '12px' }} />
                  <div>
                    <h2 style={{ fontSize: '24px', fontWeight: '800', margin: 0, lineHeight: '1.2' }}>
                      Schema Editor
                    </h2>
                    <p style={{ fontSize: '14px', opacity: 0.9, margin: 0 }}>
                      <strong>{editingSchema.collection.name}</strong> • {editingSchema.collectionId} • Version {schemaFormData.version}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditingSchema(null);
                    resetSchemaForm();
                  }}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    padding: '8px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Enhanced Mode Toggle and Controls - Fixed */}
            <div style={{ 
              padding: '16px 32px', 
              borderBottom: '1px solid #e2e8f0',
              flexShrink: 0,
              backgroundColor: '#f8fafc'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                    {isRawMode ? 'Raw JSON Editor' : 'Visual Schema Builder'} • {Object.keys(schemaFormData.fields).length} fields
                  </h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {!isRawMode && (
                    <div style={{ position: 'relative' }}>
                      <Search size={16} style={{ 
                        position: 'absolute', 
                        left: '12px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        color: '#9ca3af' 
                      }} />
                      <input
                        type="text"
                        placeholder="Search fields..."
                        value={fieldSearchTerm}
                        onChange={(e) => setFieldSearchTerm(e.target.value)}
                        style={{
                          padding: '8px 12px 8px 36px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          width: '200px',
                          transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    </div>
                  )}
                  <button
                    onClick={toggleSchemaMode}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      backgroundColor: isRawMode ? '#059669' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isRawMode ? <Eye size={16} /> : <Code size={16} />}
                    <span style={{ marginLeft: '8px' }}>
                      {isRawMode ? 'Visual Editor' : 'Raw JSON'}
                    </span>
                  </button>
                  {!isRawMode && (
                    <button
                      onClick={addSchemaField}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Plus size={16} style={{ marginRight: '8px' }} />
                      Add Field
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div style={{ 
              flex: 1, 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* JSON Error Display */}
              {jsonError && (
                <div style={{ 
                  margin: '16px 32px', 
                  padding: '12px 16px', 
                  backgroundColor: '#fef2f2', 
                  border: '1px solid #fca5a5', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexShrink: 0
                }}>
                  <AlertCircle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
                  <div>
                    <span style={{ fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>JSON Error: </span>
                    <span style={{ fontSize: '14px', color: '#991b1b' }}>{jsonError}</span>
                  </div>
                </div>
              )}

              {/* Schema Editor Content - Optimized */}
              <div style={{ 
                flex: 1,
                overflow: 'auto',
                padding: '0 32px 32px',
                scrollBehavior: 'smooth'
              }}>
                {isRawMode ? (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '16px',
                      padding: '12px 16px',
                      backgroundColor: '#1e293b',
                      borderRadius: '8px 8px 0 0',
                      color: 'white'
                    }}>
                      <Code size={18} style={{ marginRight: '8px' }} />
                      <h3 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                        JSON Schema Editor
                      </h3>
                    </div>
                    <div style={{ flex: 1, position: 'relative', minHeight: '400px' }}>
                      <textarea
                        value={rawSchemaJson}
                        onChange={(e) => handleRawJsonChange(e.target.value)}
                        style={{
                          width: '100%',
                          height: '100%',
                          minHeight: '500px',
                          padding: '20px',
                          border: '2px solid #e2e8f0',
                          borderRadius: '0 0 8px 8px',
                          fontSize: '14px',
                          fontFamily: 'JetBrains Mono, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          backgroundColor: '#fafafa',
                          resize: 'none',
                          lineHeight: '1.6',
                          transition: 'border-color 0.2s ease',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        placeholder="Enter schema JSON..."
                      />
                    </div>
                    <div style={{ 
                      marginTop: '12px',
                      padding: '12px 16px',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #bae6fd'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Info size={16} style={{ color: '#0284c7', flexShrink: 0 }} />
                        <p style={{ fontSize: '13px', color: '#075985', margin: 0, lineHeight: '1.4' }}>
                          Edit the schema directly in JSON format. Ensure valid JSON structure before switching back to visual mode.
                        </p>
                      </div>
                    </div>
                  </div>

) : (
  <div>
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      marginBottom: '24px',
      paddingTop: '16px'
    }}>
      <h3 style={{ 
        fontSize: '18px', 
        fontWeight: '700', 
        color: '#1e293b', 
        margin: 0
      }}>
        Schema Fields
      </h3>
      <div style={{
        fontSize: '14px',
        color: '#64748b'
      }}>
        {fieldSearchTerm && (
          <span>
            Showing {filteredSchemaFields.length} of {Object.keys(schemaFormData.fields).length} fields
          </span>
        )}
      </div>
    </div>
    
    {/* Optimized Field Rendering */}
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: '16px'
    }}>
      {filteredSchemaFields.length > 0 ? (
        filteredSchemaFields.map(([fieldName, field]) => (
          <div key={fieldName} style={{ willChange: 'transform' }}>
            <SchemaFieldEditor 
              fieldName={fieldName} 
              field={field}
              expandedFields={expandedFields}
              onToggleExpansion={toggleFieldExpansion}
              onUpdateField={updateSchemaField}
              onRemoveField={removeSchemaField}
              onAddNestedField={addNestedObjectField}
              createSchemaField={DataService.createSchemaField}
            />
          </div>
        ))
      ) : Object.keys(schemaFormData.fields).length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '64px 32px',
          backgroundColor: '#f8fafc',
          borderRadius: '16px',
          border: '2px dashed #cbd5e1'
        }}>
          <FileText size={48} style={{ color: '#cbd5e1', margin: '0 auto 16px' }} />
          <h4 style={{ fontSize: '20px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
            No Schema Fields
          </h4>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
            Start building your schema by adding your first field. Define the structure and validation rules for your data.
          </p>
          <button
            onClick={addSchemaField}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <Plus size={20} style={{ marginRight: '8px' }} />
            Add First Field
          </button>
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '48px 32px',
          backgroundColor: '#f8fafc',
          borderRadius: '16px',
          border: '1px solid #e2e8f0'
        }}>
          <Search size={32} style={{ color: '#cbd5e1', margin: '0 auto 16px' }} />
          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
            No Fields Match Search
          </h4>
          <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>
            No fields match "{fieldSearchTerm}". Try adjusting your search terms.
          </p>
          <button
            onClick={() => setFieldSearchTerm('')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Clear Search
          </button>
        </div>
      )}
    </div>
  </div>
)}
</div>
</div>

{/* Enhanced Modal Footer - Fixed */}
<div style={{ 
padding: '16px 32px', 
borderTop: '1px solid #e2e8f0', 
flexShrink: 0,
backgroundColor: 'white',
boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
}}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
  {Object.keys(schemaFormData.fields).length} fields • Version {schemaFormData.version}
</div>
<div style={{ display: 'flex', gap: '12px' }}>
  <button
    onClick={() => {
      setEditingSchema(null);
      resetSchemaForm();
    }}
    style={{
      padding: '10px 20px',
      color: '#475569',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      backgroundColor: 'white',
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }}
    disabled={loading}
  >
    Cancel
  </button>
  <button
    onClick={handleUpdateSchema}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '10px 20px',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      opacity: loading ? 0.7 : 1
    }}
    disabled={loading}
  >
    <Save size={16} style={{ marginRight: '8px' }} />
    {loading ? 'Saving...' : 'Save Schema'}
  </button>
</div>
</div>
</div>
</div>
</div>
)}

{/* Enhanced Create/Edit Organization Modal */}
{(isCreateModalOpen || editingOrg) && (
<div style={{
position: 'fixed',
inset: 0,
backgroundColor: 'rgba(15, 23, 42, 0.8)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
padding: '20px',
zIndex: 50,
backdropFilter: 'blur(8px)'
}}>
<div style={{
backgroundColor: 'white',
borderRadius: '24px',
maxWidth: '800px',
width: '100%',
maxHeight: '90vh',
overflow: 'auto',
boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
border: '1px solid #e2e8f0'
}}>
{/* Enhanced Header */}
<div style={{ 
padding: '32px 32px 24px', 
borderBottom: '1px solid #e2e8f0',
background: editingOrg ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
color: 'white',
borderRadius: '24px 24px 0 0'
}}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
<div>
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
    <Building2 size={28} style={{ marginRight: '12px' }} />
    <h2 style={{ fontSize: '28px', fontWeight: '800', margin: 0 }}>
      {editingOrg ? 'Edit Organization' : 'Create Organization'}
    </h2>
  </div>
  <p style={{ fontSize: '16px', opacity: 0.9, margin: 0 }}>
    {editingOrg ? 'Update organization settings and configuration' : 'Set up a new organization with collections and roles'}
  </p>
</div>
<button
  onClick={() => {
    setIsCreateModalOpen(false);
    setEditingOrg(null);
    resetForm();
  }}
  style={{
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    borderRadius: '12px',
    padding: '12px',
    transition: 'all 0.2s ease'
  }}
  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
>
  <X size={24} />
</button>
</div>
</div>

<div style={{ padding: '32px' }}>
{/* Basic Info Section */}
<div style={{ marginBottom: '32px' }}>
<h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
  Basic Information
</h3>
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
  <div>
    <label style={{ 
      display: 'block', 
      fontSize: '14px', 
      fontWeight: '600', 
      color: '#374151', 
      marginBottom: '8px'
    }}>
      Organization ID {!editingOrg && <span style={{ color: '#dc2626' }}>*</span>}
    </label>
    <input
      type="text"
      value={formData.id}
      onChange={(e) => setFormData({ ...formData, id: e.target.value })}
      style={{
        width: '100%',
        padding: '12px 16px',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '16px',
        transition: 'all 0.2s ease',
        backgroundColor: editingOrg ? '#f8fafc' : 'white'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#3b82f6';
        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#e2e8f0';
        e.target.style.boxShadow = 'none';
      }}
      placeholder="Enter unique organization ID"
      disabled={!!editingOrg}
    />
    {editingOrg && (
      <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
        Organization ID cannot be changed after creation
      </p>
    )}
  </div>
  <div>
    <label style={{ 
      display: 'block', 
      fontSize: '14px', 
      fontWeight: '600', 
      color: '#374151', 
      marginBottom: '8px'
    }}>
      Organization Name <span style={{ color: '#dc2626' }}>*</span>
    </label>
    <input
      type="text"
      value={formData.name}
      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
      style={{
        width: '100%',
        padding: '12px 16px',
        border: '2px solid #e2e8f0',
        borderRadius: '12px',
        fontSize: '16px',
        transition: 'all 0.2s ease'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#3b82f6';
        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#e2e8f0';
        e.target.style.boxShadow = 'none';
      }}
      placeholder="Enter organization display name"
    />
  </div>
</div>
</div>

{/* Enhanced Collections Section */}
<div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', margin: 0 }}>
                    <Database size={24} style={{ marginRight: '12px', color: '#64748b' }} />
                    Collections
                  </h3>
                  <button
                    onClick={addCollection}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
                  >
                    <Plus size={16} style={{ marginRight: '6px' }} />
                    Add Collection
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {formData.collections.map((collection, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      padding: '16px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      alignItems: 'center'
                    }}>
                      <div style={{ flex: 1 }}>
                        <input
                          type="text"
                          value={collection.id}
                          onChange={(e) => updateCollection(index, 'id', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                          placeholder="Collection ID"
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <input
                          type="text"
                          value={collection.name}
                          onChange={(e) => updateCollection(index, 'name', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                          placeholder="Collection Name"
                        />
                      </div>
                      <button
                        onClick={() => removeCollection(index)}
                        style={{
                          padding: '10px',
                          color: '#ef4444',
                          backgroundColor: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#fee2e2';
                          e.currentTarget.style.borderColor = '#fca5a5';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                          e.currentTarget.style.borderColor = '#fecaca';
                        }}
                        title="Remove collection"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {formData.collections.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '32px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      border: '2px dashed #cbd5e1'
                    }}>
                      <Database size={32} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
                      <p style={{ fontSize: '14px', color: '#64748b', margin: 0, fontStyle: 'italic' }}>
                        No collections added yet. Click "Add Collection" to get started.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Roles Section */}
              <div style={{ marginBottom: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', margin: 0 }}>
                    <Users size={24} style={{ marginRight: '12px', color: '#64748b' }} />
                    Roles
                  </h3>
                  <button
                    onClick={addRole}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#3b82f6'}
                  >
                    <Plus size={16} style={{ marginRight: '6px' }} />
                    Add Role
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                  {formData.roles.map((role) => (
                    <div key={role} style={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      padding: '8px 16px',
                      borderRadius: '16px',
                      border: '2px solid #93c5fd',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      <span>{role}</span>
                      {!DEFAULT_ROLES.includes(role) && (
                        <button
                          onClick={() => removeRole(role)}
                          style={{
                            marginLeft: '8px',
                            color: '#1e40af',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '2px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(30, 64, 175, 0.1)';
                            e.currentTarget.style.color = '#1d4ed8';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#1e40af';
                          }}
                          title="Remove role"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {formData.roles.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '24px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '2px dashed #cbd5e1'
                  }}>
                    <Users size={24} style={{ color: '#cbd5e1', margin: '0 auto 8px' }} />
                    <p style={{ fontSize: '14px', color: '#64748b', margin: 0, fontStyle: 'italic' }}>
                      No roles configured. Default roles will be applied.
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                <button
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setEditingOrg(null);
                    resetForm();
                  }}
                  style={{
                    padding: '12px 24px',
                    color: '#475569',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={editingOrg ? handleUpdateOrganization : handleCreateOrganization}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '12px 24px',
                    backgroundColor: editingOrg ? '#f59e0b' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.7 : 1,
                    boxShadow: editingOrg ? '0 4px 12px rgba(245, 158, 11, 0.3)' : '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = editingOrg ? '#d97706' : '#2563eb';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = editingOrg ? '0 8px 20px rgba(245, 158, 11, 0.4)' : '0 8px 20px rgba(59, 130, 246, 0.4)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = editingOrg ? '#f59e0b' : '#3b82f6';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = editingOrg ? '0 4px 12px rgba(245, 158, 11, 0.3)' : '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }
                  }}
                  disabled={loading}
                >
                  <Save size={20} style={{ marginRight: '8px' }} />
                  {loading ? 'Saving...' : editingOrg ? 'Update Organization' : 'Create Organization'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Delete Confirmation Modal */}
      {deleteConfirmOrg && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 50,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            maxWidth: '480px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ padding: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px' }}>
                <div style={{
                  flexShrink: 0,
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#fef2f2',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px'
                }}>
                  <Trash2 size={24} style={{ color: '#ef4444' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                    Delete Organization
                  </h3>
                  <p style={{ fontSize: '16px', color: '#64748b', margin: 0, lineHeight: '1.5' }}>
                    This action cannot be undone. This will permanently delete the organization and all associated data.
                  </p>
                </div>
              </div>
              
              <div style={{ marginBottom: '32px' }}>
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '2px solid #fecaca',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <h4 style={{ fontSize: '16px', color: '#991b1b', fontWeight: '600', marginBottom: '8px' }}>
                    Organization: {deleteConfirmOrg}
                  </h4>
                  <ul style={{ fontSize: '14px', color: '#dc2626', margin: 0, paddingLeft: '20px', lineHeight: '1.6' }}>
                    <li>All organization data will be permanently deleted</li>
                    <li>All collections and schemas will be removed</li>
                    <li>All user permissions will be revoked</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={() => setDeleteConfirmOrg(null)}
                  style={{
                    padding: '12px 24px',
                    color: '#475569',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteOrganization(deleteConfirmOrg)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '12px 24px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: loading ? 0.7 : 1,
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#dc2626';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.4)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = '#ef4444';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                    }
                  }}
                  disabled={loading}
                >
                  <Trash2 size={20} style={{ marginRight: '8px' }} />
                  {loading ? 'Deleting...' : 'Delete Organization'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add optimized animations and scrolling */}
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          .schema-field-card {
            transition: all 0.2s ease;
            contain: layout style paint;
          }
          
          .schema-field-card:hover {
            border-color: #3b82f6 !important;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15) !important;
          }
          
          .top-level:hover {
            transform: translateY(-2px);
          }
          
          /* Optimize scrolling performance */
          * {
            -webkit-overflow-scrolling: touch;
          }
          
          /* Custom scrollbar for better UX */
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          
          ::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
    </div>
  );
};

export default OrganizationsManager;